import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Card,
  Table,
  Typography,
  message,
  Button,
  Row,
  Col,
  Input,
  Select,
  Popover,
  Checkbox,
  Space,
  Modal,
  Form,
} from 'antd';
import { DownloadOutlined, EllipsisOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getPortfolioList, getAccounts, getCurrencies, createPortfolio } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { formatMoney, formatUnits } from '../utils/labels';

const { Title, Text } = Typography;
const { Option } = Select;

const TOGGLEABLE_COLUMNS = [
  { key: 'account', label: 'Account' },
  { key: 'rc', label: 'Reporting CCY' },
  { key: 'total_nav', label: 'Total NAV' },
  { key: 'unit_nav', label: 'Unit NAV' },
  { key: 'units', label: 'Units' },
  { key: 'custodian', label: 'Custodian' },
  { key: 'custodian_account_code', label: 'Custodian Account Code' },
];

const DEFAULT_VISIBLE = TOGGLEABLE_COLUMNS.map((c) => c.key);

const formatNumber = (raw, fractionDigits) => {
  if (fractionDigits > 2) return formatUnits(raw, { fractionDigits });
  return formatMoney(raw, { fractionDigits });
};

const columnStorageKey = (userKey) => `portfolioList.visibleColumns.${userKey || 'anonymous'}`;

const loadVisibleColumns = (userKey) => {
  try {
    const raw = localStorage.getItem(columnStorageKey(userKey));
    if (!raw) return [...DEFAULT_VISIBLE];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [...DEFAULT_VISIBLE];
    const allowed = new Set(DEFAULT_VISIBLE);
    const next = parsed.filter((k) => allowed.has(k));
    return next.length ? next : [...DEFAULT_VISIBLE];
  } catch {
    return [...DEFAULT_VISIBLE];
  }
};

const PortfolioList = () => {
  const { user, userRole } = useAuth();
  const userKey = user?.username || user?.email || userRole || 'anonymous';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [accountFilter, setAccountFilter] = useState('all');
  const [reportingCcyFilter, setReportingCcyFilter] = useState('all');
  const [visibleColumnKeys, setVisibleColumnKeys] = useState(() => loadVisibleColumns(userKey));
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm] = Form.useForm();
  const navigate = useNavigate();

  useEffect(() => {
    setVisibleColumnKeys(loadVisibleColumns(userKey));
  }, [userKey]);

  const persistVisibleColumns = useCallback(
    (keys) => {
      setVisibleColumnKeys(keys);
      try {
        localStorage.setItem(columnStorageKey(userKey), JSON.stringify(keys));
      } catch {
        // ignore quota / private mode
      }
    },
    [userKey]
  );

  const loadMeta = useCallback(async () => {
    try {
      const [accList, ccyData] = await Promise.all([getAccounts(), getCurrencies(1, 200)]);
      const ccyList = Array.isArray(ccyData) ? ccyData : (ccyData.results || []);
      setAccounts(Array.isArray(accList) ? accList : []);
      setCurrencies(ccyList);
    } catch {
      message.error('Failed to load filter options');
    }
  }, []);

  const loadPortfolios = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getPortfolioList({
        search: appliedSearch,
        account: accountFilter,
        reporting_currency: reportingCcyFilter,
      });
      setRows(Array.isArray(data) ? data : []);
    } catch {
      message.error('Failed to load portfolios');
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, accountFilter, reportingCcyFilter]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  useEffect(() => {
    loadPortfolios();
  }, [loadPortfolios]);

  const onSearch = (value) => {
    setAppliedSearch((value || '').trim());
  };

  const onAccountChange = (value) => {
    setAccountFilter(value);
  };

  const onReportingCcyChange = (value) => {
    setReportingCcyFilter(value);
  };

  const exportCsv = () => {
    const headers = [
      'Portfolio',
      'Account',
      'Account number',
      'Reporting CCY',
      'Total NAV',
      'Unit NAV',
      'Units',
      'Custodian',
      'Custodian account code',
    ];
    const lines = [headers.join(',')];
    rows.forEach((r) => {
      const cells = [
        r.name,
        r.account?.name || '',
        r.account?.account_number || '',
        r.reporting_currency?.code || '',
        r.total_nav ?? '',
        r.unit_nav ?? '',
        r.units_outstanding ?? '',
        r.custodian || '',
        r.custodian_account_code || '',
      ].map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`);
      lines.push(cells.join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `portfolios-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCreate = async () => {
    try {
      const values = await createForm.validateFields();
      setCreating(true);
      const created = await createPortfolio({
        name: values.name,
        account_id: values.account_id,
        reporting_currency_id: values.reporting_currency_id,
        cash_balance: values.cash_balance ?? 0,
        custodian: values.custodian || 'other',
        custodian_account_code: values.custodian_account_code || '',
      });
      message.success('Portfolio created');
      setCreateOpen(false);
      createForm.resetFields();
      await loadPortfolios();
      if (created?.id) navigate(`/portfolios/${created.id}`);
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.response?.data?.name?.[0] || e.response?.data?.error || 'Create failed');
    } finally {
      setCreating(false);
    }
  };

  const allColumns = useMemo(
    () => [
      {
        title: 'Portfolio',
        key: 'name',
        fixed: 'left',
        render: (_, r) => r.name,
      },
      {
        title: 'Account',
        key: 'account',
        render: (_, r) =>
          r.account?.name ? `${r.account.name} (${r.account.account_number || ''})` : '—',
      },
      {
        title: 'Reporting CCY',
        key: 'rc',
        render: (_, r) => r.reporting_currency?.code || '—',
      },
      {
        title: 'Total NAV',
        key: 'total_nav',
        align: 'right',
        render: (_, r) => formatNumber(r.total_nav, 2),
      },
      {
        title: 'Unit NAV',
        key: 'unit_nav',
        align: 'right',
        render: (_, r) => {
          if (!r.unitized || r.unit_nav == null || r.unit_nav === '') return '—';
          return formatNumber(r.unit_nav, 2);
        },
      },
      {
        title: 'Units',
        key: 'units',
        align: 'right',
        render: (_, r) => {
          if (!r.unitized) return '—';
          return formatNumber(r.units_outstanding, 4);
        },
      },
      {
        title: 'Custodian',
        key: 'custodian',
        render: (_, r) => (r.custodian || '—').toUpperCase(),
      },
      {
        title: 'Custodian Account Code',
        key: 'custodian_account_code',
        render: (_, r) => r.custodian_account_code || '—',
      },
      {
        title: '',
        key: 'act',
        fixed: 'right',
        width: 80,
        render: (_, r) => (
          <Button type="link" onClick={() => navigate(`/portfolios/${r.id}`)}>
            Open
          </Button>
        ),
      },
    ],
    [navigate]
  );

  const visibleSet = useMemo(() => new Set(visibleColumnKeys), [visibleColumnKeys]);

  const columns = useMemo(
    () =>
      allColumns.filter((col) => {
        if (col.key === 'name' || col.key === 'act') return true;
        return visibleSet.has(col.key);
      }),
    [allColumns, visibleSet]
  );

  const columnPicker = (
    <div style={{ minWidth: 220 }}>
      <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
        Show columns
      </Text>
      <Checkbox.Group
        style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
        options={TOGGLEABLE_COLUMNS.map((c) => ({ label: c.label, value: c.key }))}
        value={visibleColumnKeys}
        onChange={(keys) => persistVisibleColumns(keys)}
      />
      <Button
        type="link"
        size="small"
        style={{ paddingLeft: 0, marginTop: 8 }}
        onClick={() => persistVisibleColumns([...DEFAULT_VISIBLE])}
      >
        Reset to default
      </Button>
    </div>
  );

  return (
    <Card>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            Portfolios
          </Title>
        </Col>
        <Col>
          <Space wrap>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
              Add portfolio
            </Button>
            <Button icon={<DownloadOutlined />} onClick={exportCsv} disabled={!rows.length}>
              Export CSV
            </Button>
            <Popover
              content={columnPicker}
              title="Columns"
              trigger="click"
              placement="bottomRight"
            >
              <Button icon={<EllipsisOutlined />} aria-label="Column settings" />
            </Popover>
            <Button onClick={() => loadPortfolios()} loading={loading}>
              Refresh
            </Button>
          </Space>
        </Col>
      </Row>

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={10}>
          <Input.Search
            placeholder="Search by portfolio/account/currency/custodian/code"
            allowClear
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onSearch={onSearch}
            enterButton
          />
        </Col>
        <Col xs={24} sm={12} md={7}>
          <Select
            style={{ width: '100%' }}
            value={accountFilter}
            onChange={onAccountChange}
            placeholder="Account"
            showSearch
            optionFilterProp="children"
          >
            <Option value="all">All accounts</Option>
            {accounts.map((a) => (
              <Option key={a.id} value={a.id}>
                {a.name} ({a.account_number})
              </Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={7}>
          <Select
            style={{ width: '100%' }}
            value={reportingCcyFilter}
            onChange={onReportingCcyChange}
            placeholder="Reporting currency"
            showSearch
            optionFilterProp="children"
          >
            <Option value="all">All reporting currencies</Option>
            {currencies
              .filter((c) => c.is_active !== false)
              .map((c) => (
                <Option key={c.id} value={c.code}>
                  {c.code} — {c.name || c.code}
                </Option>
              ))}
          </Select>
        </Col>
      </Row>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        scroll={{ x: true }}
        pagination={{ pageSize: 20, showTotal: (t) => `${t} portfolios` }}
      />

      <Modal
        title="Add portfolio"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={handleCreate}
        confirmLoading={creating}
        destroyOnClose
      >
        <Form form={createForm} layout="vertical" initialValues={{ cash_balance: 0, custodian: 'other' }}>
          <Form.Item
            name="name"
            label="Name"
            rules={[
              { required: true, message: 'Name is required' },
              { min: 3, message: 'At least 3 characters' },
            ]}
          >
            <Input placeholder="Portfolio name" />
          </Form.Item>
          <Form.Item
            name="account_id"
            label="Account"
            rules={[{ required: true, message: 'Select an account' }]}
          >
            <Select
              showSearch
              optionFilterProp="children"
              placeholder="Account"
            >
              {accounts.map((a) => (
                <Option key={a.id} value={a.id}>
                  {a.name} ({a.account_number})
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="reporting_currency_id" label="Reporting currency">
            <Select
              allowClear
              showSearch
              optionFilterProp="children"
              placeholder="Defaults to USD"
            >
              {currencies
                .filter((c) => c.is_active !== false)
                .map((c) => (
                  <Option key={c.id} value={c.id}>
                    {c.code} — {c.name || c.code}
                  </Option>
                ))}
            </Select>
          </Form.Item>
          <Form.Item name="cash_balance" label="Opening cash">
            <Input type="number" min={0} step="0.01" />
          </Form.Item>
          <Form.Item name="custodian_account_code" label="Custodian account code">
            <Input placeholder="Optional" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default PortfolioList;
